import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import {
  FiUploadCloud,
  FiSend,
  FiXCircle,
  FiChevronDown,
  FiUpload,
  FiLink,
  FiPlus,
  FiSave,
  FiRefreshCw,
  FiCopy,
} from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import { ref, set, push, get, getDatabase } from "firebase/database";
import { db } from "./firebase/config";
import { useLocation, useNavigate } from "react-router-dom";
import { resizeImage, processMultipleImages } from "./utils/imageProcessor";
import {
  POST_LENGTHS,
  POST_STYLES,
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
} from "./constants/postSettings";
import ConfirmDialog from "./components/ConfirmDialog";
import {
  getProductAccounts,
  getSocialAccountKey,
  loadConnectedSocialAccounts,
  toLegacyFacebookFanpagePayload,
  toSelectedAccountPayload,
} from "./utils/socialChannels";

const ProductCreateScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const existingProduct = location.state?.product || null;
  // Đảm bảo lấy đúng ID từ sản phẩm hiện có
  const [productId] = useState(existingProduct?.id || null);
  const [productName, setProductName] = useState(existingProduct?.name || "");
  const [productInfo, setProductInfo] = useState(existingProduct?.info || "");
  const [firstCommentText, setFirstCommentText] = useState(
    existingProduct?.firstCommentText || ""
  );
  const [selectedImages, setSelectedImages] = useState(
    existingProduct?.images || []
  );
  const [imageUrl, setImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState(
    getProductAccounts(existingProduct)[0] || null
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [previewImage, setPreviewImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [postLength, setPostLength] = useState(DEFAULT_POST_LENGTH);
  const [postStyles, setPostStyles] = useState(DEFAULT_POST_STYLES);
  const [includeContactInfo, setIncludeContactInfo] = useState(false);
  const [useFanpageInfo, setUseFanpageInfo] = useState(false);

  const [isActive, setIsActive] = useState(existingProduct?.isActive ?? true);
  const [isPosted, setIsPosted] = useState(existingProduct?.isPosted ?? false);

  const [postUrl, setPostUrl] = useState("");

  // States cho chức năng clone
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [selectedCloneFanpages, setSelectedCloneFanpages] = useState([]);
  const [isCloning, setIsCloning] = useState(false);
  const [isCloneDropdownOpen, setIsCloneDropdownOpen] = useState(false);
  const cloneDropdownRef = useRef(null);

  // States cho tính năng logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPosition, setLogoPosition] = useState("top-right");
  const [logoOpacity, setLogoOpacity] = useState(0.8);
  const [logoSize, setLogoSize] = useState(20); // percentage of image width
  const logoInputRef = useRef(null);

  // States cho confirm dialogs
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPostedConfirm, setShowResetPostedConfirm] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    const loadFanpages = async () => {
      try {
        const db = getDatabase();
        const userId = localStorage.getItem("userId");
        const accountsFromFirebase = await loadConnectedSocialAccounts(db, userId);

        if (accountsFromFirebase.length > 0) {
          console.log("Accounts được tải từ Firebase:", accountsFromFirebase);
          setFanpages(accountsFromFirebase);

          const existingAccount = getProductAccounts(existingProduct)[0] || null;
          if (existingAccount) {
            const existingKey = getSocialAccountKey(existingAccount);
            const savedAccount = accountsFromFirebase.find(
              (account) => getSocialAccountKey(account) === existingKey
            );
            if (savedAccount) {
              setSelectedFanpages(savedAccount);
              return;
            }
          }

          if (!selectedFanpages && accountsFromFirebase.length === 1) {
            setSelectedFanpages(accountsFromFirebase[0]);
          }
        } else {
          console.error("Không tìm thấy account Facebook/Zalo trong Firebase");
        }
      } catch (error) {
        console.error("Lỗi khi tải accounts từ Firebase:", error);
        toast.error(
          "Có lỗi khi tải dữ liệu tài khoản đăng bài. Vui lòng thử lại sau!",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    };

    loadFanpages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        cloneDropdownRef.current &&
        !cloneDropdownRef.current.contains(event.target)
      ) {
        setIsCloneDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleFanpageSelect = (fanpage) => {
    setSelectedFanpages(fanpage);
    setIsDropdownOpen(false);
    console.log("Đã chọn account:", fanpage.name);

    if ((fanpage.platform || "facebook") !== "facebook") {
      return;
    }

    // Lưu thông tin fanpage Facebook đã chọn vào localStorage để dễ truy cập
    try {
      const db = getDatabase();
      const userId = localStorage.getItem("userId");

      // Lấy thông tin fanpage từ Firebase nếu có
      const fanpageInfoRef = ref(db, `users/${userId}/fanpages/${fanpage.id}`);
      get(fanpageInfoRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const fanpageInfo = snapshot.val();
            // Lưu vào localStorage để tiện sử dụng
            const storedFanpages = JSON.parse(
              localStorage.getItem("fanpages") || "{}"
            );
            storedFanpages[fanpage.id] = { ...fanpageInfo };
            localStorage.setItem("fanpages", JSON.stringify(storedFanpages));
          }
        })
        .catch((error) => {
          console.error("Lỗi khi đọc thông tin fanpage từ Firebase:", error);
        });
    } catch (error) {
      console.error("Lỗi khi lưu thông tin fanpage đã chọn:", error);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    setIsUploading(true);

    try {
      // Chỉ resize ảnh, không thêm logo ngay lúc này
      // Logo sẽ được thêm khi lưu sản phẩm trong handleSubmit
      const resizedFiles = await Promise.all(
        files.map((file) => resizeImage(file))
      );

      setSelectedImages((prevImages) => {
        const newImages = [...prevImages, ...resizedFiles];
        return newImages.slice(0, 9);
      });
    } catch (error) {
      console.error("Lỗi khi xử lý ảnh:", error);
      toast.error("Có lỗi xảy ra khi xử lý ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files);
    setIsUploading(true);

    try {
      const resizedFiles = await Promise.all(
        files.map((file) => resizeImage(file))
      );
      setSelectedImages((prevImages) => {
        const newImages = [...prevImages, ...resizedFiles];
        return newImages.slice(0, 9); // Giới hạn 4 ảnh
      });
    } catch (error) {
      console.error("Lỗi khi xử lý ảnh:", error);
      toast.error("Có lỗi xảy ra khi xử lý ảnh!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Handlers cho logo
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Kiểm tra kích thước file (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Logo không được vượt quá 5MB!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Kiểm tra định dạng file
      if (!file.type.startsWith("image/")) {
        toast.error("Vui lòng chọn file ảnh cho logo!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      setLogoFile(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleLogoUploadClick = () => {
    logoInputRef.current.click();
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleAddImageUrl = () => {
    if (!imageUrl.trim()) return;

    // Kiểm tra URL hợp lệ
    try {
      new URL(imageUrl);
    } catch (e) {
      toast.error("URL ảnh không hợp lệ!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setSelectedImages((prevImages) => {
      const newImages = [...prevImages, imageUrl];
      return newImages.slice(0, 9); // Giới hạn 4 ảnh
    });
    setImageUrl("");
  };

  const handleSubmit = async () => {
    if (!productName) {
      toast.error("Vui lòng nhập tên sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!selectedFanpages) {
      toast.error("Vui lòng chọn Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsUploading(true);

    const imageUrls = new Set();
    for (const image of selectedImages.filter(Boolean)) {
      if (typeof image === "string") {
        imageUrls.add(image);
        continue;
      }

      // Thêm logo vào ảnh trước khi upload nếu có logo
      let processedImage = image;
      if (logoFile) {
        try {
          processedImage = await addLogoToImage(
            image,
            logoFile,
            logoPosition,
            logoOpacity,
            logoSize
          );
        } catch (logoError) {
          console.error("Error adding logo to image:", logoError);
          // Tiếp tục với ảnh gốc nếu thêm logo thất bại
        }
      }

      const formData = new FormData();
      formData.append("file", processedImage);
      formData.append("upload_preset", "AutoWork");
      formData.append("api_key", "238276161954221");

      try {
        const response = await axios.post(
          "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        imageUrls.add(response.data.secure_url);
      } catch (error) {
        console.error("Failed to upload image:", error);
        toast.error("Failed to upload image!", {
          position: "top-right",
          autoClose: 3000,
        });
        setIsUploading(false);
        return;
      }
    }

    const userId = localStorage.getItem("userId");

    try {
      let productRef;
      let productData;
      const selectedAccount = toSelectedAccountPayload(selectedFanpages);
      const legacyFanpages =
        selectedAccount.platform === "facebook"
          ? [toLegacyFacebookFanpagePayload(selectedFanpages)]
          : [];
      const preservedOrdering = {
        ...(existingProduct?.position !== undefined && {
          position: existingProduct.position,
        }),
        ...(existingProduct?.fanpagePositions && {
          fanpagePositions: existingProduct.fanpagePositions,
        }),
      };

      if (existingProduct) {
        // Cập nhật sản phẩm hiện có
        productRef = ref(db, `products/${userId}/${existingProduct.id}`);
        productData = {
          id: existingProduct.id,
          name: productName,
          info: productInfo,
          firstCommentText: firstCommentText,
          images: Array.from(imageUrls),
          selectedAccounts: [selectedAccount],
          fanpages: legacyFanpages,
          isActive: isActive,
          isPosted: isPosted,
          ...preservedOrdering,
        };
      } else {
        // Tạo sản phẩm mới
        const newProductRef = push(ref(db, `products/${userId}`));
        productRef = newProductRef;
        productData = {
          id: newProductRef.key,
          name: productName,
          info: productInfo,
          firstCommentText: firstCommentText,
          images: Array.from(imageUrls),
          selectedAccounts: [selectedAccount],
          fanpages: legacyFanpages,
          isActive: isActive,
          isPosted: false,
        };
      }

      await set(productRef, productData);

      toast.success(
        existingProduct
          ? "Sản phẩm đã được cập nhật thành công!"
          : "Sản phẩm đã được thêm thành công!",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      if (!existingProduct) {
        setProductName("");
        setProductInfo("");
        setFirstCommentText("");
        setSelectedImages([]);
        setSelectedFanpages(null);
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      toast.error("Failed to save product!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Function thêm logo vào ảnh
  const addLogoToImage = async (
    imageFile,
    logoFile,
    position,
    opacity,
    sizePercent
  ) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Set canvas size to match image
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // If no logo, return original image
        if (!logoFile) {
          canvas.toBlob(resolve, "image/jpeg", 0.9);
          return;
        }

        const logoImg = new Image();
        logoImg.onload = () => {
          // Calculate logo size (percentage of image width)
          const logoWidth = (img.width * sizePercent) / 100;
          const logoHeight = (logoImg.height * logoWidth) / logoImg.width;

          // Calculate logo position
          let x, y;
          const padding = 20; // padding from edges

          switch (position) {
            case "top-left":
              x = padding;
              y = padding;
              break;
            case "top-right":
              x = img.width - logoWidth - padding;
              y = padding;
              break;
            case "center":
              x = (img.width - logoWidth) / 2;
              y = (img.height - logoHeight) / 2;
              break;
            case "bottom-left":
              x = padding;
              y = img.height - logoHeight - padding;
              break;
            case "bottom-right":
              x = img.width - logoWidth - padding;
              y = img.height - logoHeight - padding;
              break;
            default:
              x = img.width - logoWidth - padding;
              y = padding;
          }

          // Draw logo with opacity
          ctx.globalAlpha = opacity;
          ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
          ctx.globalAlpha = 1; // Reset alpha

          // Convert to blob
          canvas.toBlob(resolve, "image/jpeg", 0.9);
        };

        logoImg.onerror = () => {
          reject(new Error("Không thể tải logo"));
        };

        logoImg.src = URL.createObjectURL(logoFile);
      };

      img.onerror = () => {
        reject(new Error("Không thể tải ảnh"));
      };

      img.src = URL.createObjectURL(imageFile);
    });
  };

  const handleCreatePost = async () => {
    if (selectedImages.length === 0) {
      toast.error("Vui lòng chọn ít nhất một ảnh để tạo bài viết!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const processedImages = await processMultipleImages(selectedImages);

    navigate("/photos-post", {
      state: {
        images: processedImages,
        productInfo: `Tên sản phẩm: ${productName}\n${productInfo}`,
      },
    });
  };

  // Hàm mở modal clone
  const handleCloneProduct = () => {
    if (!existingProduct) {
      toast.error("Chỉ có thể clone sản phẩm đã được lưu!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Lọc ra các fanpages khác (không phải fanpage hiện tại)
    const selectedAccountKey = selectedFanpages
      ? getSocialAccountKey(selectedFanpages)
      : "";
    const availableCloneFanpages = fanpages.filter(
      (fanpage) => getSocialAccountKey(fanpage) !== selectedAccountKey
    );

    if (availableCloneFanpages.length === 0) {
      toast.error("Không có fanpage khác để clone sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsCloneModalOpen(true);
    setSelectedCloneFanpages([]);
  };

  // Hàm thực hiện clone sản phẩm
  const handleConfirmClone = async () => {
    if (selectedCloneFanpages.length === 0) {
      toast.error("Vui lòng chọn ít nhất một fanpage để clone sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsCloning(true);

    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();

      // Tạo sản phẩm mới cho từng fanpage được chọn
      const clonePromises = selectedCloneFanpages.map(async (fanpage) => {
        const newProductRef = push(ref(db, `products/${userId}`));
        const selectedAccount = toSelectedAccountPayload(fanpage);
        const legacyFanpages =
          selectedAccount.platform === "facebook"
            ? [toLegacyFacebookFanpagePayload(fanpage)]
            : [];
        const clonedProductData = {
          id: newProductRef.key,
          name: `${productName} (Clone cho ${fanpage.name})`,
          info: productInfo,
          firstCommentText: firstCommentText,
          images: selectedImages, // Copy ảnh
          selectedAccounts: [selectedAccount],
          fanpages: legacyFanpages,
          isActive: true,
          isPosted: false,
          // Copy các settings khác
          postLength: postLength,
          postStyles: postStyles.join(", "),
          includeContactInfo: includeContactInfo,
          useFanpageInfo: useFanpageInfo,
        };

        await set(newProductRef, clonedProductData);
        return fanpage.name;
      });

      const clonedFanpageNames = await Promise.all(clonePromises);

      toast.success(
        `Đã clone sản phẩm thành công cho ${
          clonedFanpageNames.length
        } fanpage: ${clonedFanpageNames.join(", ")}!`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );

      // Đóng modal và reset state
      setIsCloneModalOpen(false);
      setSelectedCloneFanpages([]);
    } catch (error) {
      console.error("Failed to clone product:", error);
      toast.error("Không thể clone sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsCloning(false);
    }
  };

  // Hàm đóng modal clone
  const handleCloseCloneModal = () => {
    setIsCloneModalOpen(false);
    setSelectedCloneFanpages([]);
    setIsCloneDropdownOpen(false);
  };

  // Hàm toggle chọn fanpage cho clone
  const handleToggleCloneFanpage = (fanpage) => {
    setSelectedCloneFanpages((prev) => {
      const fanpageKey = getSocialAccountKey(fanpage);
      const isAlreadySelected = prev.some(
        (f) => getSocialAccountKey(f) === fanpageKey
      );
      if (isAlreadySelected) {
        return prev.filter((f) => getSocialAccountKey(f) !== fanpageKey);
      } else {
        return [...prev, fanpage];
      }
    });
  };

  // Hàm chọn tất cả fanpage
  const handleSelectAllCloneFanpages = () => {
    const selectedAccountKey = selectedFanpages
      ? getSocialAccountKey(selectedFanpages)
      : "";
    const availableFanpages = fanpages.filter(
      (fanpage) => getSocialAccountKey(fanpage) !== selectedAccountKey
    );
    setSelectedCloneFanpages(availableFanpages);
  };

  // Hàm bỏ chọn tất cả fanpage
  const handleDeselectAllCloneFanpages = () => {
    setSelectedCloneFanpages([]);
  };

  const openImagePreview = (imageUrl) => {
    const index = selectedImages.findIndex((img) => {
      if (typeof img === "string") {
        return img === imageUrl;
      }
      return URL.createObjectURL(img) === imageUrl;
    });
    setCurrentImageIndex(index);
    setPreviewImage(imageUrl);
  };

  const closeImagePreview = () => {
    setPreviewImage(null);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    const newIndex =
      (currentImageIndex - 1 + selectedImages.length) % selectedImages.length;
    setCurrentImageIndex(newIndex);
    const newImage = selectedImages[newIndex];
    setPreviewImage(
      typeof newImage === "string" ? newImage : URL.createObjectURL(newImage)
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    const newIndex = (currentImageIndex + 1) % selectedImages.length;
    setCurrentImageIndex(newIndex);
    const newImage = selectedImages[newIndex];
    setPreviewImage(
      typeof newImage === "string" ? newImage : URL.createObjectURL(newImage)
    );
  };

  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const settingsRef = ref(
          db,
          `users/${userId}/settings/productCreateScreen`
        );
        const snapshot = await get(settingsRef);

        if (snapshot.exists()) {
          const settings = snapshot.val();
          setPostLength(settings.postLength || DEFAULT_POST_LENGTH);
          setPostStyles(settings.postStyles || DEFAULT_POST_STYLES);
          setIncludeContactInfo(settings.includeContactInfo || false);
          setUseFanpageInfo(settings.useFanpageInfo || false);

          // Load logo settings
          setLogoPosition(settings.logoPosition || "top-right");
          setLogoOpacity(settings.logoOpacity || 0.8);
          setLogoSize(settings.logoSize || 20);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
      }
    };

    loadUserSettings();
  }, []);

  const saveUserSettings = async () => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      const db = getDatabase();
      const settingsRef = ref(
        db,
        `users/${userId}/settings/productCreateScreen`
      );

      const settings = {
        postLength,
        postStyles,
        includeContactInfo,
        useFanpageInfo,
        logoPosition,
        logoOpacity,
        logoSize,
      };

      await set(settingsRef, settings);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  const handlePostStyleChange = (style) => {
    const newStyles = postStyles.includes(style)
      ? postStyles.filter((s) => s !== style)
      : [...postStyles, style];
    setPostStyles(newStyles);
    saveUserSettings();
  };

  const handleIncludeContactInfoChange = (e) => {
    setIncludeContactInfo(e.target.checked);
    saveUserSettings();
  };

  const handleLogoPositionChange = (position) => {
    setLogoPosition(position);
    saveUserSettings();
  };

  const handleLogoOpacityChange = (opacity) => {
    setLogoOpacity(opacity);
    saveUserSettings();
  };

  const handleLogoSizeChange = (size) => {
    setLogoSize(size);
    saveUserSettings();
  };

  const handleGetImagesFromPost = async () => {
    if (!postUrl || !selectedFanpages) {
      toast.error("Vui lòng nhập URL bài post và chọn Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if ((selectedFanpages.platform || "facebook") !== "facebook") {
      toast.error("Chức năng lấy ảnh từ bài post chỉ hỗ trợ Facebook Fanpage!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      // Lấy post ID từ URL
      let postId;
      let urlParams;

      if (postUrl.includes("permalink.php") || postUrl.includes("story.php")) {
        try {
          // Xử lý cả 2 loại URL format
          const url = new URL(postUrl);
          urlParams = new URLSearchParams(url.search);
          const storyFbid = urlParams.get("story_fbid");
          const pageId = urlParams.get("id");

          if (!storyFbid || !pageId) {
            throw new Error("URL không hợp lệ");
          }
          postId = storyFbid;
        } catch (error) {
          throw new Error("URL không hợp lệ");
        }
      } else {
        // Format cũ: .../posts/{postId}
        const postIdMatch = postUrl.match(/\/posts\/(\d+)/);
        if (!postIdMatch) {
          throw new Error("URL không hợp lệ");
        }
        postId = postIdMatch[1];
      }

      const pageId = selectedFanpages.id;
      const pageAccessToken = selectedFanpages.access_token;

      const response = await fetch(
        `https://graph.facebook.com/v21.0/${pageId}_${postId}?fields=id,message,attachments{subattachments{media}},created_time,permalink_url,full_picture&access_token=${pageAccessToken}`
      );

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error.message);
      }

      // Lấy danh sách URL ảnh từ response
      let images = [];

      // Kiểm tra attachments trước
      if (data.attachments?.data[0]?.subattachments?.data) {
        images = data.attachments.data[0].subattachments.data.map(
          (item) => item.media.image.src
        );
      }

      // Nếu không có ảnh trong attachments, kiểm tra full_picture
      if (images.length === 0 && data.full_picture) {
        images = [data.full_picture];
      }

      if (images.length === 0) {
        toast.warning("Không tìm thấy ảnh trong bài post!", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // Thêm các ảnh vào danh sách đã chọn
      setSelectedImages((prevImages) => {
        const newImages = [...prevImages, ...images];
        return newImages.slice(0, 9); // Giới hạn 4 ảnh
      });

      setPostUrl("");
      toast.success("Đã thêm ảnh từ bài post thành công!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error fetching post images:", error);
      toast.error("Không thể lấy ảnh từ bài post: " + error.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleDeleteProduct = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setIsUploading(true);
    const userId = localStorage.getItem("userId");

    try {
      await set(
        ref(db, `products/${userId}/${existingProduct.id}`),
        null
      );

      toast.success("Đã xóa sản phẩm thành công!", {
        position: "top-right",
        autoClose: 3000,
      });

      setTimeout(() => {
        navigate("/products");
      }, 1000);
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error("Không thể xóa sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleResetPostedStatus = () => {
    if (!existingProduct || !existingProduct.id) {
      toast.error("Không tìm thấy thông tin sản phẩm!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setShowResetPostedConfirm(true);
  };

  const handleConfirmResetPosted = async () => {
    setIsUploading(true);
    const userId = localStorage.getItem("userId");

    try {
      // Cập nhật trạng thái isPosted thành false
      await set(
        ref(db, `products/${userId}/${existingProduct.id}/isPosted`),
        false
      );

      // Cập nhật state local
      setIsPosted(false);

      toast.success("Đã loại bỏ trạng thái đã đăng! Sản phẩm có thể tạo bài viết mới.", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Failed to reset posted status:", error);
      toast.error("Không thể loại bỏ trạng thái đã đăng!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-5">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          {existingProduct ? "Chỉnh Sửa Sản Phẩm" : "Thêm Sản Phẩm"}
        </h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Phần trái - Form nhập thông tin */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Thông tin sản phẩm
                </h2>
                <a
                  href="https://www.youtube.com/watch?v=dHgoo9YANEY&t=1s&ab_channel=AUTOWORK"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-md shadow-sm transition duration-300 hover:text-red-600"
                  title="Xem video hướng dẫn cách sử dụng"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-600"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                  <span>Hướng dẫn</span>
                </a>
              </div>

              {/* Khu vực kéo thả ảnh */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline-block mb-1"
                  >
                    <path
                      d="M4 16L8.586 11.414C8.96106 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.0389 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.9611 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.0389 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="inline-block ml-1">Tải ảnh sản phẩm</span>
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
                    ${
                      isUploading
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/30"
                    }
                    ${selectedImages.length > 0 ? "pb-24" : ""}
                  `}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {selectedImages.length === 0 ? (
                    <>
                      <div className="mx-auto w-20 h-20 mb-4 flex items-center justify-center rounded-full bg-blue-50">
                        <FiUpload className="text-3xl text-blue-500" />
                      </div>
                      <p className="text-base text-gray-600 font-medium">
                        Kéo thả ảnh vào đây hoặc
                      </p>
                      <button
                        type="button"
                        className="mt-2 inline-flex items-center text-blue-600 font-medium hover:text-blue-700"
                      >
                        Chọn ảnh từ máy tính
                      </button>
                      <p className="mt-2 text-sm text-gray-500">
                        Định dạng: JPG, PNG, JPEG (tối đa 5MB/ảnh)
                      </p>
                    </>
                  ) : (
                    <div className="py-2">
                      {/* Hiển thị hướng dẫn thêm ảnh */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex-1 flex flex-col justify-center items-center sm:items-start">
                          <p className="font-medium text-gray-700 mb-1 text-center sm:text-left">
                            Thêm ảnh mới
                          </p>
                          <p className="text-xs text-gray-500 text-center sm:text-left">
                            Nhấn nút hoặc kéo thả vào khu vực này
                          </p>
                        </div>
                        <div className="flex flex-row flex-wrap justify-center gap-2">
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                          >
                            <FiUpload className="text-gray-500" />
                            <span>Từ máy tính</span>
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 bg-white border border-gray-300 rounded text-gray-700 flex items-center gap-1 hover:bg-gray-50 text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              document
                                .getElementById("image-url-input")
                                .focus();
                            }}
                          >
                            <FiLink className="text-gray-500" />
                            <span>Từ URL</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hiển thị ảnh đã tải lên */}
              {selectedImages.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">
                      Danh sách ảnh ({selectedImages.length})
                    </label>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={
                            typeof image === "string"
                              ? image
                              : URL.createObjectURL(image)
                          }
                          alt={`Selected ${index}`}
                          className="w-full h-full object-contain bg-gray-100 rounded-lg"
                        />
                        <FiXCircle
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-2 right-2 text-red-600 cursor-pointer z-10"
                          size={20}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openImagePreview(
                              typeof image === "string"
                                ? image
                                : URL.createObjectURL(image)
                            );
                          }}
                          className="absolute top-2 left-2 bg-gray-800 bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full z-10"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path
                              fillRule="evenodd"
                              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}

                    {selectedImages.length < 4 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current.click();
                        }}
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30"
                      >
                        <FiPlus className="text-2xl text-gray-400" />
                        <span className="text-sm text-gray-500 mt-1">
                          Thêm ảnh
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Thêm ảnh từ URL */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.1901 8.7127L15.4701 10.9927M8.5601 13.3327L11.1901 15.9627M7.9201 8.2127H7.9301M18.0001 19.0027C19.6569 19.0027 21.0001 17.6596 21.0001 16.0027C21.0001 14.3459 19.6569 13.0027 18.0001 13.0027C16.3432 13.0027 15.0001 14.3459 15.0001 16.0027C15.0001 17.6596 16.3432 19.0027 18.0001 19.0027ZM6.0001 19.0027C7.65685 19.0027 9.0001 17.6596 9.0001 16.0027C9.0001 14.3459 7.65685 13.0027 6.0001 13.0027C4.34335 13.0027 3.0001 14.3459 3.0001 16.0027C3.0001 17.6596 4.34335 19.0027 6.0001 19.0027ZM12.0001 11.0027C13.6569 11.0027 15.0001 9.6596 15.0001 8.0027C15.0001 6.34585 13.6569 5.0027 12.0001 5.0027C10.3432 5.0027 9.0001 6.34585 9.0001 8.0027C9.0001 9.6596 10.3432 11.0027 12.0001 11.0027Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label
                    className="text-gray-700 font-medium"
                    id="image-url-label"
                  >
                    Thêm ảnh từ URL
                  </label>
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLink className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="image-url-input"
                    type="text"
                    className="w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    aria-labelledby="image-url-label"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddImageUrl();
                      }}
                      disabled={!imageUrl.trim()}
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !imageUrl.trim()
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Thêm
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Nhập liên kết trực tiếp đến ảnh (URL phải kết thúc bằng .jpg,
                  .jpeg, .png)
                </p>
              </div>

              {/* Thêm ảnh từ bài post Facebook */}
              {(!selectedFanpages ||
                (selectedFanpages.platform || "facebook") === "facebook") && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 9.68586V12.9999M12 16.3139H12.01M6.4142 20.0001H17.5858C17.851 20.0001 18.1054 19.8947 18.2929 19.7072L19.7071 18.293C19.8946 18.1055 20 17.851 20 17.5859V6.41414C20 6.149 19.8946 5.89464 19.7071 5.70711L18.2929 4.29289C18.1054 4.10536 17.851 4 17.5858 4H6.4142C6.149 4 5.89464 4.10536 5.70711 4.29289L4.29289 5.70711C4.10536 5.89464 4 6.149 4 6.41414V17.5859C4 17.851 4.10536 18.1055 4.29289 18.293L5.70711 19.7072C5.89464 19.8947 6.14899 20.0001 6.4142 20.0001Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label className="text-gray-700 font-medium">
                    Thêm ảnh từ bài post Facebook
                  </label>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="#1877F2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C15.9164 21.5878 18.0622 20.3855 19.6099 18.57C21.1576 16.7546 22.0054 14.4456 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    className="w-full pl-10 pr-28 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    placeholder="https://www.facebook.com/..."
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      onClick={handleGetImagesFromPost}
                      disabled={
                        !postUrl.trim() ||
                        !selectedFanpages ||
                        fanpages.length === 0
                      }
                      className={`h-full px-4 rounded-r-md text-white font-medium transition-colors ${
                        !postUrl.trim() ||
                        !selectedFanpages ||
                        fanpages.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      Lấy ảnh
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  Nhập URL bài post để lấy ảnh (cần chọn Fanpage trước)
                </p>
              </div>
              )}

              {/* Cài đặt logo tự động */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                      stroke="#4B5563"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <label className="text-gray-700 font-medium">
                    Logo tự động cho sản phẩm
                  </label>
                </div>

                {/* Upload logo */}
                <div className="mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      onClick={handleLogoUploadClick}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <FiUpload className="text-blue-600" />
                      <span className="text-sm font-medium">
                        {logoFile ? "Thay đổi logo" : "Tải lên logo"}
                      </span>
                    </button>

                    {logoFile && (
                      <button
                        onClick={handleRemoveLogo}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md hover:bg-red-100 transition-colors"
                      >
                        <FiXCircle className="text-red-600" />
                        <span className="text-sm font-medium">Xóa logo</span>
                      </button>
                    )}
                  </div>

                  {logoFile && (
                    <div className="mt-3 flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="w-12 h-12 bg-white border border-gray-200 rounded flex items-center justify-center overflow-hidden">
                        <img
                          src={URL.createObjectURL(logoFile)}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">
                          Logo đã được tải lên: {logoFile.name}
                        </p>
                        <p className="text-xs text-green-600">
                          Logo sẽ được tự động thêm vào tất cả ảnh sản phẩm khi
                          lưu
                        </p>
                      </div>
                    </div>
                  )}

                  {!logoFile && (
                    <p className="mt-2 text-xs text-gray-500">
                      Tải lên logo để tự động thêm vào ảnh sản phẩm khi upload
                    </p>
                  )}
                </div>

                {/* Cài đặt vị trí và kích thước logo */}
                {logoFile && (
                  <div className="space-y-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                    {/* Vị trí logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vị trí logo
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "top-left", label: "Trên trái" },
                          { value: "top-right", label: "Trên phải" },
                          { value: "center", label: "Chính giữa" },
                          { value: "bottom-left", label: "Dưới trái" },
                          { value: "bottom-right", label: "Dưới phải" },
                        ].map((position) => (
                          <label
                            key={position.value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="logoPosition"
                              value={position.value}
                              checked={logoPosition === position.value}
                              onChange={(e) =>
                                handleLogoPositionChange(e.target.value)
                              }
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">
                              {position.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Kích thước logo */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kích thước logo: {logoSize}%
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        value={logoSize}
                        onChange={(e) =>
                          handleLogoSizeChange(Number(e.target.value))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>5%</span>
                        <span>50%</span>
                      </div>
                    </div>

                    {/* Độ trong suốt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Độ trong suốt: {Math.round(logoOpacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.1"
                        value={logoOpacity}
                        onChange={(e) =>
                          handleLogoOpacityChange(Number(e.target.value))
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>10%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Phần phải - Cấu hình đăng bài */}
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-lg shadow-md p-5 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Cấu hình đăng bài
              </h2>

              {/* Chọn Fanpage/Zalo OA */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Chọn Fanpage/Zalo OA để đăng bài
                </label>
                {fanpages.length === 0 ? (
                  <button
                    onClick={() => {
                      navigate("/profile");
                      setTimeout(() => {
                        const element = document.getElementById(
                          "facebook-fanpage-management"
                        );
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                        }
                      }, 500);
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm sm:text-base"
                  >
                    Thiết lập tài khoản để đăng bài
                  </button>
                ) : (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={toggleDropdown}
                      type="button"
                      className="w-full flex justify-between items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                    >
                      <span>
                        {selectedFanpages
                          ? selectedFanpages.name
                          : "Chọn Fanpage/Zalo OA"}
                      </span>
                      <FiChevronDown className="w-5 h-5 text-gray-400" />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {fanpages.map((fanpage) => (
                          <div
                            key={getSocialAccountKey(fanpage)}
                            onClick={() => handleFanpageSelect(fanpage)}
                            className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                              selectedFanpages &&
                              getSocialAccountKey(selectedFanpages) ===
                                getSocialAccountKey(fanpage)
                                ? "bg-blue-50 text-blue-600"
                                : ""
                            }`}
                          >
                            <span>{fanpage.name}</span>
                            <span className="float-right text-xs text-gray-500 uppercase">
                              {fanpage.platform === "zalo" ? "Zalo OA" : "Facebook"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tên sản phẩm */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Tên sản phẩm
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Nhập tên sản phẩm..."
                />
              </div>

              {/* Thông tin sản phẩm */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Thông tin sản phẩm
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  value={productInfo}
                  onChange={(e) => setProductInfo(e.target.value)}
                  placeholder="Nhập thông tin sản phẩm..."
                ></textarea>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Comment đầu tiên (không bắt buộc)
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  value={firstCommentText}
                  onChange={(e) => setFirstCommentText(e.target.value)}
                  placeholder="Ví dụ: Link sản phẩm / link affiliate sẽ được comment ngay sau khi bài đăng thành công"
                ></textarea>
                <p className="mt-1 text-sm text-gray-500">
                  Nếu thiết lập lịch đăng cũng có comment mặc định, nội dung ở
                  sản phẩm này sẽ được ưu tiên dùng trước.
                </p>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <label className="block text-gray-700 font-medium">
                    Tự động đăng bài từ sản phẩm
                  </label>
                  <div className="relative inline-block w-12 mr-2 align-middle select-none">
                    <input
                      type="checkbox"
                      name="autoPost"
                      id="autoPost"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="hidden"
                    />
                    <label
                      htmlFor="autoPost"
                      className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out
                        ${isActive ? "bg-blue-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full shadow transform transition-transform duration-200 ease-in-out
                          ${
                            isActive
                              ? "translate-x-6 bg-white border-2 border-blue-500"
                              : "translate-x-0 bg-white"
                          }`}
                      />
                    </label>
                  </div>
                </div>
                {isActive ? (
                  <p className="mt-2 text-sm text-gray-600">
                    Sản phẩm này sẽ được hệ thống chọn đăng khi fanpage có thiết
                    lập tự động đăng bài từ sản phẩm đang bật.
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">
                    Sản phẩm này sẽ bị bỏ qua trong chức năng tự động đăng bài
                    từ sản phẩm, dù fanpage vẫn có lịch đăng.
                  </p>
                )}
              </div>

              {/* Hiển thị trạng thái đã đăng và nút reset */}
              {existingProduct && isPosted && (
                <div className="mb-6">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800 mb-2">
                          Sản phẩm này đã được đăng
                        </p>
                        <p className="text-xs text-yellow-700 mb-3">
                          Sản phẩm này đã được hệ thống đăng bài. Nếu bạn muốn tạo bài viết mới cho sản phẩm này, vui lòng loại bỏ trạng thái đã đăng.
                        </p>
                        <button
                          type="button"
                          onClick={handleResetPostedStatus}
                          disabled={isUploading}
                          className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          <FiRefreshCw className="w-4 h-4" />
                          Loại bỏ trạng thái đã đăng
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-row gap-3 mt-6">
                <button
                  onClick={handleSubmit}
                  disabled={isUploading || fanpages.length === 0}
                  className="flex-1 flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                  title={
                    fanpages.length === 0
                      ? "Vui lòng thiết lập fanpage trước"
                      : "Lưu thông tin sản phẩm hiện tại"
                  }
                >
                  {isUploading ? (
                    <ClipLoader size={20} color={"#fff"} className="mr-2" />
                  ) : (
                    <FiSave className="mr-2" />
                  )}
                  {isUploading
                    ? "Đang lưu..."
                    : existingProduct
                    ? "Lưu thay đổi"
                    : "Thêm sản phẩm"}
                </button>

                {existingProduct && (
                  <>
                    <button
                      onClick={handleCreatePost}
                      disabled={isUploading || fanpages.length === 0}
                      className="flex-1 flex items-center justify-center bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                      title={
                        fanpages.length === 0
                          ? "Vui lòng thiết lập fanpage trước"
                          : "Tạo bài viết mới từ thông tin sản phẩm này"
                      }
                    >
                      <FiSend className="mr-2" />
                      Tạo bài viết mới
                    </button>

                    <button
                      onClick={handleCloneProduct}
                      disabled={isUploading || fanpages.length <= 1}
                      className="flex-1 flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                      title={
                        fanpages.length <= 1
                          ? "Cần có ít nhất 2 fanpage để clone"
                          : "Clone sản phẩm cho fanpage khác"
                      }
                    >
                      <FiCopy className="mr-2" />
                      Clone sản phẩm
                    </button>

                    <button
                      onClick={handleDeleteProduct}
                      disabled={isUploading}
                      className="flex-1 flex items-center justify-center border border-red-600 text-red-600 py-2 px-4 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                      title="Xóa sản phẩm khỏi hệ thống"
                    >
                      {isUploading ? (
                        <ClipLoader
                          size={20}
                          color={"#f87171"}
                          className="mr-2"
                        />
                      ) : (
                        <FiXCircle className="mr-2" />
                      )}
                      Xóa sản phẩm
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={closeImagePreview}
        >
          <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
            {/* Previous button */}
            <button
              onClick={handlePrevImage}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Close button */}
            <button
              onClick={closeImagePreview}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Image */}
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[90vh] max-w-full w-auto h-auto object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next button */}
            <button
              onClick={handleNextImage}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full z-20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Image counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full z-20">
              {currentImageIndex + 1} / {selectedImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Modal Clone Sản phẩm */}
      {isCloneModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Clone Sản phẩm
              </h3>
              <button
                onClick={handleCloseCloneModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <MdClose size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Chọn các fanpage để clone sản phẩm "{productName}" sang:
                  </p>

                  {/* Buttons để chọn tất cả / bỏ chọn tất cả */}
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={handleSelectAllCloneFanpages}
                      disabled={isCloning}
                      className="px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 font-medium"
                    >
                      Chọn tất cả
                    </button>
                    <button
                      onClick={handleDeselectAllCloneFanpages}
                      disabled={isCloning}
                      className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 font-medium"
                    >
                      Bỏ chọn tất cả
                    </button>
                  </div>

                  {/* Danh sách tài khoản với checkbox */}
                  <div className="border border-gray-300 rounded-md max-h-96 overflow-y-auto">
                    {fanpages
                      .filter(
                        (fanpage) =>
                          !selectedFanpages ||
                          getSocialAccountKey(fanpage) !==
                            getSocialAccountKey(selectedFanpages)
                      )
                      .map((fanpage) => {
                        const fanpageKey = getSocialAccountKey(fanpage);
                        return (
                        <div
                          key={fanpageKey}
                          className="flex items-center p-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            id={`clone-fanpage-${fanpageKey}`}
                            checked={selectedCloneFanpages.some(
                              (f) => getSocialAccountKey(f) === fanpageKey
                            )}
                            onChange={() => handleToggleCloneFanpage(fanpage)}
                            disabled={isCloning}
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <label
                            htmlFor={`clone-fanpage-${fanpageKey}`}
                            className="ml-4 flex-1 cursor-pointer"
                          >
                            <div className="text-left">
                              <span className="font-medium text-gray-900 text-base">
                                {fanpage.name}
                              </span>
                              <span className="ml-2 text-xs text-gray-500 uppercase">
                                {fanpage.platform === "zalo" ? "Zalo OA" : "Facebook"}
                              </span>
                              {fanpage.category && (
                                <p className="text-sm text-gray-500 mt-1">
                                  {fanpage.category}
                                </p>
                              )}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  {/* Hiển thị thông tin sản phẩm sẽ được tạo */}
                  {selectedCloneFanpages.length > 0 && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
                      <p className="text-sm text-blue-700 font-medium mb-2">
                        Sẽ tạo {selectedCloneFanpages.length} sản phẩm mới:
                      </p>
                      <div className="max-h-32 overflow-y-auto">
                        <ul className="text-sm text-blue-600 space-y-1">
                          {selectedCloneFanpages.map((fanpage) => (
                            <li
                              key={getSocialAccountKey(fanpage)}
                              className="flex items-start"
                            >
                              <span className="text-blue-500 mr-2 mt-0.5">
                                •
                              </span>
                              <span>
                                "{productName} (Clone cho {fanpage.name})"
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-xs text-blue-600 mt-3 font-medium">
                        Tất cả thông tin sẽ được copy sang các fanpage mới với
                        trạng thái "Chưa đăng"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 flex justify-end space-x-3 p-6 border-t border-gray-200 bg-white">
              <button
                onClick={handleCloseCloneModal}
                disabled={isCloning}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 font-medium"
              >
                Hủy
              </button>
              <button
                onClick={handleConfirmClone}
                disabled={selectedCloneFanpages.length === 0 || isCloning}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center font-medium"
              >
                {isCloning ? (
                  <>
                    <ClipLoader size={16} color="#fff" className="mr-2" />
                    Đang clone...
                  </>
                ) : (
                  <>
                    <FiCopy className="mr-2" />
                    Clone{" "}
                    {selectedCloneFanpages.length > 0
                      ? `(${selectedCloneFanpages.length})`
                      : ""}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />

      {/* Confirm Delete Product Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa sản phẩm"
        message="Bạn có chắc chắn muốn xóa sản phẩm này? Hành động này không thể hoàn tác."
        confirmText="Xóa sản phẩm"
        cancelText="Hủy bỏ"
        type="danger"
      />

      {/* Confirm Reset Posted Status Dialog */}
      <ConfirmDialog
        isOpen={showResetPostedConfirm}
        onClose={() => setShowResetPostedConfirm(false)}
        onConfirm={handleConfirmResetPosted}
        title="Xác nhận loại bỏ trạng thái đã đăng"
        message="Bạn có chắc chắn muốn loại bỏ trạng thái đã đăng? Sản phẩm này sẽ được đăng lại trong chu kỳ tiếp theo."
        confirmText="Loại bỏ trạng thái"
        cancelText="Giữ nguyên"
        type="warning"
      />
    </div>
  );
};

export default ProductCreateScreen;
